import { useMemo, useRef, useState } from "react";
import { Controller, FieldValues } from "react-hook-form";

import { InputLabelHeight } from "@covertree/types";
import { getInputLabelHeight } from "@covertree/utils";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { debounce, DebouncedFunc } from "lodash";

import { TestableListboxProps } from "../FormAutocomplete";
import { FormEmailAutocorrectProps } from "./types";
import { useValidateEmailLazyQuery } from "@covertree/new-business-graphql";
import { Tooltip } from "../../content";

export let getNewSuggestionsFn: DebouncedFunc<(...args: unknown[]) => unknown>;

export function FormEmailAutocorrect<TValues extends FieldValues>({
  id,
  name,
  control,
  hideSuggestion,
  label,
  labelHeight = InputLabelHeight.DEFAULT,
  placeholder,
  setValue,
  dataTestId,
  tooltip,
  isEnabled = true,
  disabled,
  ...autocompleteProps
}: FormEmailAutocorrectProps<TValues>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<string[]>([]);
  const labelRef = useRef<HTMLParagraphElement>(null);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );

  const [validateEmail] = useValidateEmailLazyQuery();

  getNewSuggestionsFn = useMemo(
    () =>
      debounce(async (email: string) => {
        if (email && email.length >= 5) {
          setLoading(true);
          setValue(
            name as TValues[keyof TValues],
            email as TValues[keyof TValues],
            { shouldValidate: true, shouldDirty: true }
          );

          try {
            const response = await validateEmail({
              variables: {
                email,
              },
            });

            const autocorrect = response.data?.validateEmail.autocorrect;
            setOptions(
              autocorrect && autocorrect.length > 0 ? [autocorrect] : []
            );
          } catch (e) {
            setOptions([]);
          } finally {
            setLoading(false);
          }
        }
      }, 1000) as DebouncedFunc<(...args: unknown[]) => unknown>,
    [name, setValue]
  );

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      defaultValue={"" as any}
      control={control}
      render={({ field: { value, onChange, ref }, fieldState }) => (
        <FormControl fullWidth error={fieldState.invalid}>
          {(label || tooltip) && (
            <InputLabel
              htmlFor={id}
              id={`${id}-label`}
              variant="standard"
              sx={() => ({
                height: isDesktop ? labelComponentHeight : "auto",
                alignItems: "flex-end",
                display: "flex",
                position: "static",
                transform: "none",
                "& > p": {
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  width: "100%",
                  whiteSpace: "break-spaces",
                },
              })}
              shrink>
              <Typography variant="body2" ref={labelRef}>
                {label}
                {tooltip && (
                  <Tooltip
                    dataTestId={`${dataTestId}-tooltip`}
                    tooltip={tooltip ?? ""}
                    size="large"
                    sx={{ marginLeft: theme.spacing() }}
                    placement="bottom"
                  />
                )}
              </Typography>
            </InputLabel>
          )}
          <Autocomplete
            componentsProps={{
              popper: {
                modifiers: [
                  {
                    name: "flip",
                    enabled: false,
                  },
                  {
                    name: "preventOverflow",
                    enabled: false,
                  },
                ],
              },
            }}
            renderOption={(props, optionValue) => (
              <Typography {...props} sx={{ p: 2 }}>
                Did you mean &quot;{optionValue}&quot;?
              </Typography>
            )}
            freeSolo
            id={id}
            ref={ref}
            value={value as never}
            filterOptions={(opts) => opts}
            options={hideSuggestion ? [] : options}
            disabled={disabled}
            ListboxProps={
              {
                "data-testid": "autocomplete-input-listbox",
              } as TestableListboxProps
            }
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(opt, val) => opt === val}
            loading={loading}
            loadingText={
              <Box textAlign="center">
                <CircularProgress
                  color="secondary"
                  data-testid={`${dataTestId}-loading`}
                  size={theme.spacing(3.5)}
                />
              </Box>
            }
            renderInput={({
              InputProps,
              InputLabelProps,
              inputProps,
              ...params
            }) => (
              <OutlinedInput
                {...params}
                value={value}
                placeholder={placeholder}
                inputRef={InputProps.ref}
                onChange={(event) => {
                  if (isEnabled) getNewSuggestionsFn(event.target.value);
                }}
                onBlur={(event) =>
                  setValue(
                    name as TValues[keyof TValues],
                    event.target.value as TValues[keyof TValues],
                    { shouldValidate: true, shouldDirty: true }
                  )
                }
                sx={{
                  padding: "0 !important",
                  backgroundColor: disabled ? "action.disabled" : "auto",
                }}
                inputProps={{
                  ...inputProps,
                  "aria-labelledby": `${id}-label`,
                  "data-testid": dataTestId,
                  sx: {
                    padding: "0 13px 0 14px !important",
                  },
                }}
              />
            )}
            {...autocompleteProps}
            onChange={(e, v, r, d) => {
              if (autocompleteProps.onChange) {
                autocompleteProps.onChange(e, v, r, d);
              }
              onChange(v);
            }}
          />
          <FormHelperText
            variant="outlined"
            {...(!fieldState.error?.message && { sx: { margin: 0 } })}>
            {fieldState.error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
}
